

import React from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { useTheme } from '@mui/material';
import { formatShortDate } from '../../../utils/date';
import { formatCurrency } from '../../../utils/utilityFunctions';
import { borderRadius } from '@mui/system';
import { useSelector } from 'react-redux';
import { RootStateType } from '../../../redux-store/reducers';


interface NavChartProps {
    data: Array<[number, number]>
    data1?: Array<[number, number]>
    data2?: Array<[number, number]>
    data3?: Array<[number, number]>
    data4?: Array<[number, number]>
    label1?: string;
    label2?: string;
    label3?: string;
    label4?: string;
    label5?: string;
    dynamicY?: boolean
}

export default function CustomNavChart(props: NavChartProps): JSX.Element {
    const theme = useTheme();

    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);


    const options = {
        tooltip: {
            shape: 'square',
            backgroundColor: theme.palette.customColors.cinder,
            borderRadius: 10,
            borderWidth: 0,
            style: {
                color: theme.palette.text.secondary,
                textShadow: 'none',
                fontWeight: '500',
                fontFamily: theme.typography.fontFamily,
                fontSize: '15px',
                padding: '15px'
            },
            formatter: function (this: any) {
                const date = formatShortDate(this?.x);
                const label1 = props?.label1;
                const label2 = props?.label2;
                const label3 = props?.label3;
                const label4 = props?.label4;
                const label5 = props?.label5;

                const value1 = props?.label1 && this?.points?.[0]?.y;
                const value2 = props?.label2 && this?.points?.[1]?.y;
                const value3 = props?.label3 && this?.points?.[2]?.y;
                const value4 = props?.label4 && this?.points?.[3]?.y;
                const value5 = props?.label4 && this?.points?.[4]?.y;

                const color1 = props?.label1 && this?.points[0]?.color;
                const color2 = props?.label2 && this?.points[1]?.color;
                const color3 = props?.label3 && this?.points[2]?.color;
                const color4 = props?.label4 && this?.points[3]?.color;
                const color5 = props?.label4 && this?.points[4]?.color;


                const tooltipTemp = `${props?.dynamicY ? `<div style="color:${theme.palette.common.white};">${date}</div>`
                    :
                    `<div style="color:${theme.palette.common.white};">
                        NAV: ${date}`}<br/><br/>
                    </div>
                <div style="color:${props?.dynamicY ? ((value1 ?? 0) >= 0 ? `${theme.palette.success[500]}` : `${theme.palette.error[500]}`) : `${theme.palette.grey[100]}`};font-weight: 800;">
                    ${props?.dynamicY ? `${!Number.isNaN(Number(value1 ?? 0)) ?
                        Math.round((value1 ?? 0) * 100) / 100 : '0'}%` : formatCurrency({ value: Number(this?.y), locale, currencyCode })}
                </div>`;

                const doubleLineTooltip = props?.dynamicY ? `
                <div style="color:${theme.palette.common.white};">
                ${date}
                </div>
                <br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:'${color1}';margin-right:10px;"></div>
                    <div style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">${label1}</div><br/>
                    <div style="color:${(value1 ?? 0) >= 0 ? theme.palette.success[500] : theme.palette.error[500]};font-weight: 800;font-size:14px;">
                    ${!Number.isNaN(Number(value1 ?? 0)) ?
                        Math.round((value1 ?? 0) * 100) / 100 : '0'}%</div>
                </div><br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:${color2};margin-right:10px;border-radius: 50%;"></div>
                    <div style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">${label2}<br/>
                    <div style="color:${(value2 ?? 0) >= 0 ? theme.palette.success[500] : theme.palette.error[500]};font-weight: 800;font-size:14px;">
                    ${!Number.isNaN(Number(value2 ?? 0)) ?
                        Math.round((value2 ?? 0) * 100) / 100 : '0'}%</b>
                    </div>
                </div>
                `: props.label2 !== undefined && `
                 <div style="color:${theme.palette.common.white};">
                ${date}
                </div>
                <br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:'${color1}';margin-right:10px;"></div>
                    <div style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">${label1}<br/>
                    <div style="color:${theme.palette.common.white};font-weight: 800;font-size:14px;">
                    ${formatCurrency({ value: value1, locale, currencyCode })}
                    </div>
                    </div>
                </div><br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:${color2};margin-right:10px;border-radius: 50%;"></div>
                    <div style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">${label2}<br/>
                    <div  style="color:${theme.palette.common.white};font-weight: 800;font-size:14px;">
                    ${formatCurrency({ value: value2, locale, currencyCode })}
                    </div>
                </div><br/><br/>
            `
                const tripleLineTooltip = props.label3 !== undefined && `
                <div style="color:${theme.palette.common.white};">
                ${date}
                </div>
                <br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:'${color1}';border-radius:50%;margin-right:10px;">
                    </div>
                    <div style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">${label1}<br/><b>
                    </div>
                    <div style="color:${(value1 ?? 0) >= 0 ? theme.palette.success[500] : theme.palette.error[500]};font-weight: 800;font-size:14px;"> 
                    ${!Number.isNaN(Number(value1 ?? 0)) ?
                        Math.round((value1 ?? 0) * 100) / 100 : '0'}%</b>
                    </div>
                </div>
                <br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:${color2};margin-right:10px;border-radius: 50%;"></div>
                    <div style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">${label2}<br/><b></div>
                    <div style="color:${(value2 ?? 0) >= 0 ? theme.palette.success[500] : theme.palette.error[500]};font-weight: 800;font-size:14px;"> 
                    ${!Number.isNaN(Number(value2 ?? 0)) ?
                        Math.round((value2 ?? 0) * 100) / 100 : '0'}%</b>
                    </div>
                </div>
                <br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:${color3};margin-right:10px;border-radius: 50%;"></div>
                    <div style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">${label3}<br/><b></div>
                     <div style="color:${(value3 ?? 0) >= 0 ? theme.palette.success[500] : theme.palette.error[500]};font-weight: 800;font-size:14px;">
                     ${!Number.isNaN(Number(value3 ?? 0)) ?
                        Math.round((value3 ?? 0) * 100) / 100 : '0'}%</b>
                    </div>
                </div>
                <br/><br/>
            `
                const fourLineTooltip = props.label4 !== undefined && `
                <div style="color:${theme.palette.common.white};">
                ${date}
                </div>
                <br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:'${color1}';margin-right:10px;"></div>
                    <div style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">${label1}<br/><b>
                    </div> 
                    <div style="color:${(value1 ?? 0) >= 0 ? theme.palette.success[500] : theme.palette.error[500]};font-weight: 800;font-size:14px;">
                    ${!Number.isNaN(Number(value1 ?? 0)) ?
                        Math.round((value1 ?? 0) * 100) / 100 : '0'}%</b>
                    </div>
                </div>
                <br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:${color2};margin-right:10px;border-radius: 50%;"></div>
                    <div style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">${label2}<br/><b>
                    </div>
                    <div style="color:${(value2 ?? 0) >= 0 ? theme.palette.success[500] : theme.palette.error[500]};font-weight: 800;font-size:14px;"> ${!Number.isNaN(Number(value2 ?? 0)) ?
                        Math.round((value2 ?? 0) * 100) / 100 : '0'}%</b>
                    </div>
                </div><br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:${color3};margin-right:10px;border-radius: 50%;"></div>
                    <div style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">
                    ${label3}<br/><b>
                    </div>
                    <div style="color:${(value3 ?? 0) >= 0 ? theme.palette.success[500] : theme.palette.error[500]};font-weight: 800;font-size:14px;"> ${!Number.isNaN(Number(value3 ?? 0)) ?
                        Math.round((value3 ?? 0) * 100) / 100 : '0'}%</b>
                    </div>
                </div>
                <br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:${color4};margin-right:10px;border-radius: 50%;"></div>
                    <div style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">
                    ${label4}<br/><b>
                    </div>
                    <div style="color:${(value4 ?? 0) >= 0 ? theme.palette.success[500] : theme.palette.error[500]};font-weight: 800;font-size:14px;">
                     ${!Number.isNaN(Number(value4 ?? 0)) ?
                        Math.round((value4 ?? 0) * 100) / 100 : '0'}%</b>
                    </div>
                </div>
                <br/><br/>
            `
                const fiveLineTooltip = props.label2 !== undefined && `
                <div style="color:${theme.palette.common.white};">
                ${date}
                </div>
                <br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:'${color1}';margin-right:10px;"></div>
                    <div style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">${label1}<br/><b>
                    </div>
                    <div style="color:${(value1 ?? 0) >= 0 ? theme.palette.success[500] : theme.palette.error[500]};font-weight: 800;font-size:14px;">
                     ${!Number.isNaN(Number(value1 ?? 0)) ?
                        Math.round((value1 ?? 0) * 100) / 100 : '0'}%</b>
                    </div>
                </div>
                <br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:${color2};margin-right:10px;border-radius: 50%;"></div>
                    <div style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">${label2}<br/><b>
                    </div>
                    <div style="color:${(value2 ?? 0) >= 0 ? theme.palette.success[500] : theme.palette.error[500]};font-weight: 800;font-size:14px;">
                     ${!Number.isNaN(Number(value2 ?? 0)) ?
                        Math.round((value2 ?? 0) * 100) / 100 : '0'}%</b>
                    </div>
                </div>
                <br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:${color3};margin-right:10px;border-radius: 50%;"></div>
                    <div style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">${label3}<br/><b>
                    </div>
                    <div style="color:${(value3 ?? 0) >= 0 ? theme.palette.success[500] : theme.palette.error[500]};font-weight: 800;font-size:14px;"> ${!Number.isNaN(Number(value3 ?? 0)) ?
                        Math.round((value3 ?? 0) * 100) / 100 : '0'}%</b>
                    </div>
                </div>
                <br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:${color4};margin-right:10px;border-radius: 50%;"></div>
                    <div style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">${label4}<br/><b>
                    </div>
                    <div style="color:${(value4 ?? 0) >= 0 ? theme.palette.success[500] : theme.palette.error[500]};font-weight: 800;font-size:14px;">
                     ${!Number.isNaN(Number(value4 ?? 0)) ?
                        Math.round((value4 ?? 0) * 100) / 100 : '0'}%</b>
                    </div>
                </div>
                <br/><br/>
                <div style="display:flex;align-items:center;">
                    <div style="width:20px;height:20px;background-color:${color5};margin-right:10px;border-radius: 50%;"></div>
                    <div  style="color:${theme.palette.common.white};font-weight:100;font-size:14px;">${label5}<br/><b>
                    </div>
                    <div style="color:${(value5 ?? 0) >= 0 ? theme.palette.success[500] : theme.palette.error[500]};font-weight: 800;font-size:14px;"> ${!Number.isNaN(Number(value5 ?? 0)) ?
                        Math.round((value5 ?? 0) * 100) / 100 : '0'}%</b>
                    </div>
                </div>
                <br/><br/>
            `

                return props?.label5 !== undefined ? fiveLineTooltip : props?.label4 !== undefined ? fourLineTooltip : props?.label3 !== undefined ? tripleLineTooltip : props?.label2 !== undefined ? doubleLineTooltip : tooltipTemp;

            }
        },
        credits: {
            enabled: false,
        },
        plotOptions: {
            series: {
                color: theme.palette.primary[500],

            },
        },

        yAxis: {
            opposite: false,
            labels: {
                formatter: function (this: any) {
                    const { value } = this
                    return props?.dynamicY ? `<span style="font-size: 12px">${value}%</span>` : `<span style="font-size: 12px">${formatCurrency({ value, locale, currencyCode })}</span>`
                } as any,
            },
            showLastLabel: true,
        },
        rangeSelector: {
            selected: 1,
            inputEnabled: false,
            enabled: false,

        },
        exporting: {
            enabled: false,
        },
        xAxis: {
            type: 'datetime',
            units: [
                ['millisecond', [1, 10, 100]],
                ['second', [1, 5, 15, 30]],
                ['minute', [1, 5, 15, 30]],
                ['hour', [1, 6, 12]],
                ['day', [1, 4]],
                ['week', [1]],
                ['month', [1, 3, 6]],
                ['year', [1, 2, 5]]
            ],
            dateTimeLabelFormats: {
                day: '%e %b',
                month: '%b %Y',
                year: '%Y'
            },
        },
        series:
            props.data && props?.data1 && props?.data1?.length > 0 && props.data2 && props?.data2?.length > 0 && props.data3 && props?.data3?.length > 0 && props.data4 && props?.data4?.length > 0 ? [
                {
                    name: 'NAV',
                    data: props.data,
                    tooltip: {
                        valueDecimals: 2
                    },
                    showInNavigator: false,
                    color: theme.palette.primary[500]
                },
                {
                    name: 'NAV1',
                    data: props.data1,
                    tooltip: {
                        valueDecimals: 2
                    },
                    showInNavigator: false,
                    color: theme.palette.warning[500]
                },
                {
                    name: 'NAV2',
                    data: props.data2,
                    tooltip: {
                        valueDecimals: 2
                    },
                    showInNavigator: false,
                    color: theme.palette.success[500]
                },
                {
                    name: 'NAV3',
                    data: props.data3,
                    tooltip: {
                        valueDecimals: 2
                    },
                    showInNavigator: false,
                    color: theme.palette.customColors.brightCerulean
                },
                {
                    name: 'NAV4',
                    data: props.data4,
                    tooltip: {
                        valueDecimals: 2
                    },
                    showInNavigator: false,
                    color: theme.palette.error[800],
                },
            ] : props.data && props?.data1 && props?.data1?.length > 0 && props.data2 && props?.data2?.length > 0 && props.data3 && props?.data3?.length > 0 ? [
                {
                    name: 'NAV',
                    data: props.data,
                    tooltip: {
                        valueDecimals: 2
                    },
                    showInNavigator: false,
                    color: theme.palette.primary[500]
                },
                {
                    name: 'NAV1',
                    data: props.data1,
                    tooltip: {
                        valueDecimals: 2
                    },
                    showInNavigator: false,
                    color: theme.palette.warning[500]
                },
                {
                    name: 'NAV2',
                    data: props.data2,
                    tooltip: {
                        valueDecimals: 2
                    },
                    showInNavigator: false,
                    color: theme.palette.success[500],
                },
                {
                    name: 'NAV3',
                    data: props.data3,
                    tooltip: {
                        valueDecimals: 2
                    },
                    showInNavigator: false,
                    color: theme.palette.customColors.brightCerulean
                },
            ] : props.data && props?.data1 && props?.data1?.length > 0 && props.data2 && props?.data2?.length > 0 ? [
                {
                    name: 'NAV',
                    data: props.data,
                    tooltip: {
                        valueDecimals: 2
                    },
                    showInNavigator: false,
                    color: theme.palette.primary[500]
                },
                {
                    name: 'NAV1',
                    data: props.data1,
                    tooltip: {
                        valueDecimals: 2
                    },
                    showInNavigator: false,
                    color: theme.palette.warning[500]
                },
                {
                    name: 'NAV2',
                    data: props.data2,
                    tooltip: {
                        valueDecimals: 2
                    },
                    showInNavigator: false,
                    color: theme.palette.success[500],
                }
            ] : props.data1 && props.data1?.length > 0 ?
                [
                    {
                        name: 'NAV',
                        data: props.data,
                        tooltip: {
                            valueDecimals: 2
                        },
                        showInNavigator: false,
                        color: theme.palette.primary[500]
                    },
                    {
                        name: 'NAV1',
                        data: props.data1,
                        tooltip: {
                            valueDecimals: 2
                        },
                        showInNavigator: false,
                        color: theme.palette.warning[500]
                    }
                ]
                : [
                    {
                        name: 'NAV',
                        data: props.data,
                        tooltip: {
                            valueDecimals: 2
                        },
                        showInNavigator: false,
                        color: theme.palette.primary[500]
                    },
                ],
        chart: {

            style: {
                fontFamily: `${theme.typography.fontFamily}`,
            },
            backgroundColor: `${theme.palette.common.white}`,
        },
        navigator: {
            enabled: false,
        },
        scrollbar: {
            liveRedraw: false,
            enabled: false,
        },

    };
    return (
        <HighchartsReact
            highcharts={Highcharts}
            constructorType={'stockChart'}
            options={options}
        />
    )
}